// Home.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FaApple, FaArrowLeft, FaArrowRight, FaCalendarAlt, FaChartLine, FaGooglePlay, FaHandshake, FaTimes } from "react-icons/fa";
import "./Home.css";

const images = [
  {
    src: "/images/carousel-discover-fictional.png",
    label: "For customers",
    title: "Discover local services and products",
    caption: <>Find local businesses, book services, order and pay for products<br />—all through the SnapConX app.</>,
    action: "Learn how to find services and products",
    path: "/guide/search-and-book",
    benefits: ["Local providers", "Useful search tools", "Browse before signing in"],
  },
  {
    src: "/images/carousel-booking-fictional.png",
    label: "For customers",
    title: "Book with confidence",
    caption: "Choose a service, select an available time, and keep track of your appointment updates.",
    action: "See how booking works",
    path: "/guide/search-and-book",
    benefits: ["Clear service details", "Available time slots", "Simple appointment updates"],
  },
  {
    src: "/images/carousel-business-fictional.png",
    label: "For businesses",
    title: "Build your business",
    caption: "Showcase your services and products, manage bookings, and receive product orders through SnapConX.",
    action: "Business setup guide",
    path: "/guide/create-business-profile",
    benefits: ["Business profiles", "Service management", "Customer-ready availability"],
  },
  {
    src: "/images/carousel-dashboard.svg",
    label: "Optional paid Business Dashboard",
    title: "More insight. More control.",
    caption: "Add web-based tools, analytics and reporting when your business needs more. Use the core SnapConX app without a monthly dashboard subscription.",
    action: "View Dashboard Packages",
    path: "/pricing",
    benefits: ["Business insights", "Transaction reports", "Optional dashboard subscription"],
    dashboard: true,
  },
];

const Home = () => {
  const [current, setCurrent] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isInteractionPaused, setIsInteractionPaused] = useState(false);
  const [isPaused] = useState(() => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false);
  const [isDocumentHidden, setIsDocumentHidden] = useState(document.hidden);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const animationTimer = useRef(null);

  const changeSlide = useCallback((nextSlide) => {
    setCurrent((currentSlide) => {
      const requestedSlide = typeof nextSlide === "number" ? nextSlide : currentSlide + 1;
      const next = (requestedSlide + images.length) % images.length;
      if (next === currentSlide) return currentSlide;
      setIsAnimating(true);
      window.clearTimeout(animationTimer.current);
      animationTimer.current = window.setTimeout(() => setIsAnimating(false), 300);
      return next;
    });
  }, []);

  useEffect(() => {
    const updateVisibility = () => setIsDocumentHidden(document.hidden);
    document.addEventListener("visibilitychange", updateVisibility);
    return () => document.removeEventListener("visibilitychange", updateVisibility);
  }, []);

  useEffect(() => {
    if (isPaused || isInteractionPaused || isDocumentHidden) return undefined;
    const timer = setInterval(() => {
      changeSlide();
    }, 10000);
    return () => clearInterval(timer);
  }, [changeSlide, isDocumentHidden, isInteractionPaused, isPaused]);

  useEffect(() => () => window.clearTimeout(animationTimer.current), []);

  useEffect(() => {
    if (!isDownloadModalOpen) return undefined;
    const previousFocus = document.activeElement;
    const dialog = document.querySelector('.download-modal');
    const focusable = dialog.querySelectorAll('button, a[href]');
    focusable[0].focus();
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setIsDownloadModalOpen(false);
      if (event.key === 'Tab') {
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    };

    document.addEventListener("keydown", closeOnEscape);
    return () => { document.removeEventListener("keydown", closeOnEscape); previousFocus?.focus(); };
  }, [isDownloadModalOpen]);

  const activeSlide = images[current];

  return (
    <div className="home-container">
      <div className="home-hero">
        <div
          className={`home-carousel ${activeSlide.dashboard ? "dashboard-promotion" : ""}`}
          role="region"
          aria-label="SnapConX highlights"
          onMouseEnter={() => setIsInteractionPaused(true)}
          onMouseLeave={() => setIsInteractionPaused(false)}
          onFocusCapture={() => setIsInteractionPaused(true)}
          onBlurCapture={() => setIsInteractionPaused(false)}
        >
          {images.map((img, idx) => (
            <div
              key={idx}
              className={`carousel-slide ${idx === current ? "active" : ""} ${img.dashboard ? "dashboard-visual" : ""}`}
            >
              <img
                src={img.src}
                alt=""
                className="carousel-image"
              />
              <div className="carousel-overlay" />
            </div>
          ))}
          
          <div className="carousel-content">
            <h1 className={`hero-title ${!isAnimating ? "fade-in" : ""}`}>
              {activeSlide.title}
            </h1>
            <p className={`carousel-caption ${!isAnimating ? "fade-in" : ""}`}>
              {activeSlide.caption}
            </p>
            <p className={`carousel-label ${!isAnimating ? "fade-in" : ""}`}>{activeSlide.label}</p>
            <Link to={activeSlide.path} className={`cta-button ${!isAnimating ? "fade-in" : ""}`}>
              {activeSlide.action}
            </Link>
            <ul className={`carousel-benefits ${!isAnimating ? "fade-in" : ""}`}>
              {activeSlide.benefits.map((benefit) => <li key={benefit}>{benefit}</li>)}
            </ul>
            <div className={`store-downloads ${!isAnimating ? "fade-in" : ""}`}>
              <a href="https://apps.apple.com/us/app/snapconx/id6755649903" target="_blank" rel="noopener noreferrer" className="store-link">
                <FaApple aria-hidden="true" /> <span><small>Download on the</small>App Store</span>
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.authurton.dotdashmobile" target="_blank" rel="noopener noreferrer" className="store-link">
                <FaGooglePlay aria-hidden="true" /> <span><small>Get it on</small>Google Play</span>
              </a>
            </div>
          </div>

          <div className="carousel-download-qr">
            <DownloadQr compact />
          </div>

          <div className="carousel-controls">
            <button className="carousel-arrow" onClick={() => changeSlide(current - 1)} aria-label="Show previous slide"><FaArrowLeft /></button>
            <button className="carousel-arrow" onClick={() => changeSlide(current + 1)} aria-label="Show next slide"><FaArrowRight /></button>
          </div>

        </div>
      </div>

      <section className="home-paths" aria-labelledby="home-paths-title">
        <div className="home-section-heading"><p className="home-eyebrow">Your next step</p><h2 id="home-paths-title">Local connections. More possibilities.</h2><p>Find the help you need, or help more people find your business.</p></div>
        <div className="home-path-grid">
          <article><span className="home-eyebrow">For customers</span><h3>Find services and products near you.</h3><p>Explore services and products near you. Request an appointment or order products and pay in the app.</p><Link to="/guide/search-and-book">See how to book a service <FaArrowRight aria-hidden="true" /></Link></article>
          <article><span className="home-eyebrow">For business owners</span><h3>Make your next connection count.</h3><p>Showcase your services and products, manage bookings, and receive product orders through SnapConX.</p><Link to="/guide/create-business-profile">Set up your business <FaArrowRight aria-hidden="true" /></Link><Link className="home-pricing-link" to="/pricing">Explore Dashboard Packages</Link></article>
        </div>
      </section>
      <section className="home-info">
        <p className="home-eyebrow">How it works</p>
        <h2>From discovery to your next booking or order</h2>
        <div className="underline" />
        <p className="info-description">
          Start in the SnapConX app. Browse first, then sign in when you are ready to connect.
        </p>

        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon purple">
              <FaHandshake />
            </div>
            <h3>01. Discover</h3>
            <p>Search by location or category and explore business profiles.</p>
          </div>
          
          <div className="feature-card">
            <div className="feature-icon blue">
              <FaCalendarAlt />
            </div>
            <h3>02. Book or order</h3>
            <p>Request a service appointment, or choose products to order and pay for in the app.</p>
          </div>
          
          <div className="feature-card">
            <div className="feature-icon orange">
              <FaChartLine />
            </div>
            <h3>03. Stay connected</h3>
            <p>Follow appointment updates and use Messages to speak with the business.</p>
          </div>
        </div>
      </section>

      <section className="cta-section">
        <p className="home-eyebrow">Take the next step</p>
        <h2>Bring your local world closer.</h2>
        <p>Get SnapConX for your phone, or explore our step-by-step guides before you begin.</p>
        <div className="cta-buttons">
          <button className="btn-primary" onClick={() => setIsDownloadModalOpen(true)}>Get the app</button>
          <Link to="/guide" className="btn-secondary">Explore the guides</Link>
        </div>
        <div className="home-download-options">
        <div className="store-downloads cta-store-downloads">
          <a href="https://apps.apple.com/us/app/snapconx/id6755649903" target="_blank" rel="noopener noreferrer" className="store-link">
            <FaApple aria-hidden="true" /> <span><small>Download on the</small>App Store</span>
          </a>
          <a href="https://play.google.com/store/apps/details?id=com.authurton.dotdashmobile" target="_blank" rel="noopener noreferrer" className="store-link">
            <FaGooglePlay aria-hidden="true" /> <span><small>Get it on</small>Google Play</span>
          </a>
        </div>
        <DownloadQr />
        </div>
      </section>
      {isDownloadModalOpen && (
        <div
          className="download-modal-overlay"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsDownloadModalOpen(false);
          }}
        >
          <section className="download-modal" role="dialog" aria-modal="true" aria-labelledby="download-modal-title">
            <button className="download-modal-close" onClick={() => setIsDownloadModalOpen(false)} aria-label="Close download options">
              <FaTimes aria-hidden="true" />
            </button>
            <h2 id="download-modal-title">Get the SnapConX app</h2>
            <p>Choose your device to download the SnapConX app.</p>
            <div className="download-modal-options">
              <a href="https://apps.apple.com/us/app/snapconx/id6755649903" target="_blank" rel="noopener noreferrer" className="download-modal-option">
                <FaApple aria-hidden="true" />
                <span><small>Download on the</small>App Store</span>
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.authurton.dotdashmobile" target="_blank" rel="noopener noreferrer" className="download-modal-option">
                <FaGooglePlay aria-hidden="true" />
                <span><small>Get it on</small>Google Play</span>
              </a>
            </div>
            <DownloadQr />
          </section>
        </div>
      )}
    </div>
  );
};

export default Home;

function DownloadQr({ compact = false }) {
  return (
    <figure className="home-download-qr">
      <img src="/images/snapconx-download-qr.png" width="205" height="205" alt="QR code to open SnapConX downloads for Android and iPhone" />
      <figcaption>{compact ? 'Scan to download' : 'Scan with your phone to download SnapConX'}</figcaption>
    </figure>
  );
}
